import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Camera, TrendingUp, DollarSign, Percent, ClipboardList, Zap } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [estimates, setEstimates] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/estimates").then((r) => setEstimates(r.data));
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary mb-1">Control panel</p>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">Today's jobs.</h1>
        </div>
        <Link to="/app/new-estimate">
          <Button data-testid="dashboard-new-estimate-btn" size="lg" className="h-12 rounded-sm btn-kinetic">
            <Camera className="w-4 h-4 mr-2" /> New estimate
          </Button>
        </Link>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: ClipboardList, label: "Total quotes", value: stats?.total_quotes ?? 0, testid: "stat-quotes" },
          { icon: TrendingUp, label: "Pipeline", value: fmt(stats?.pipeline_value), testid: "stat-pipeline" },
          { icon: DollarSign, label: "Revenue won", value: fmt(stats?.revenue_won), testid: "stat-revenue" },
          { icon: Percent, label: "Close rate", value: `${stats?.close_rate ?? 0}%`, testid: "stat-closerate" },
        ].map((k) => (
          <div key={k.label} data-testid={k.testid} className="bento-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</p>
              <k.icon className="w-4 h-4 text-primary" />
            </div>
            <p className="font-display text-3xl md:text-4xl font-black">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bento-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl font-bold">Revenue trend</h3>
            <span className="text-xs text-muted-foreground">Won quotes by month</span>
          </div>
          <div className="h-64">
            {stats?.revenue_series?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.revenue_series}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "4px" }} />
                  <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ fill: "hsl(var(--primary))" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No revenue data yet — close your first quote to see it here.
              </div>
            )}
          </div>
        </div>

        <div className="bento-card p-5">
          <h3 className="font-display text-2xl font-bold mb-4">Recent estimates</h3>
          {estimates.slice(0, 6).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              <Zap className="w-8 h-8 text-primary mx-auto mb-3" />
              No estimates yet. Snap your first property photo.
            </div>
          ) : (
            <ul className="space-y-2">
              {estimates.slice(0, 6).map((e) => (
                <li key={e.id}>
                  <Link
                    to={`/app/estimates/${e.id}`}
                    data-testid={`recent-est-${e.id}`}
                    className="flex items-center justify-between p-3 border border-border rounded-sm hover:bg-muted transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">{e.niche.replace(/_/g, " ")}</p>
                      <p className="text-xs text-muted-foreground">{e.status} · {new Date(e.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="font-mono font-semibold text-sm">${e.total?.toFixed(2)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
